import { t } from "../../i18n";
import { useMemo, useState } from "react";
import { fmt, type WallInput } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { useCadUi } from "../../state/cadUiStore";
import { CadDialog } from "../cad/CadDialog";
import { parseDxf, guessUnitFactor, unitsToMetre, type DxfDrawing, type Pt2 } from "../../import/dxf/parse";
import { findFaces, unionFaces, extentOf, type Face } from "../../import/dxf/loops";
import { closeSketch } from "../drawing/sketch";

type UnitChoice = "auto" | "mm" | "cm" | "m" | "in";
const UNIT_FACTOR: Record<Exclude<UnitChoice, "auto">, number> = { mm: 0.001, cm: 0.01, m: 1, in: 0.0254 };
const MAX_SEGMENTS = 20000;

/** DXF metnini çözer: UTF-8 (R2007+); ANSI_1254 kod sayfalı eski dosyada windows-1254 — sayılar her durumda ASCII, geometri bozulmaz. */
export async function readDxfText(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const utf8 = new TextDecoder("utf-8").decode(buf);
  if (!/\$DWGCODEPAGE[\s\S]{0,40}ANSI_1254/.test(utf8.slice(0, 4000))) return utf8;
  try { return new TextDecoder("windows-1254").decode(buf); } catch { return utf8; }
}

/**
 * DXF'ten kesit içe aktarma (DXFAL): dosya → doğru parçaları → kapalı bölgeler; en duvara benzeyen bölge önseçili, kullanıcı tıklayarak
 * düzeltir (bitişik bölgeler birleşir: gövde + temel); birim (mm/cm/m) $INSUNITS'ten ya da boyuttan tahmin; İçe aktar → çokgen kipi (CIZ ile aynı boru hattı).
 */
export function DxfImportDialog({ open, onClose, wall }: { open: boolean; onClose: () => void; wall: WallInput }): JSX.Element | null {
  const updateWall = useProjectStore((s) => s.updateWall);
  const pushHistory = useCadUi((s) => s.pushHistory), setViewBox = useCadUi((s) => s.setViewBox), setPropTab = useCadUi((s) => s.setPropTab);
  const [name, setName] = useState<string>("");
  const [drawing, setDrawing] = useState<DxfDrawing | null>(null);
  const [faces, setFaces] = useState<Face[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [unit, setUnit] = useState<UnitChoice>("auto");
  const [error, setError] = useState<string | null>(null);

  const onFile = async (f: File | undefined): Promise<void> => {
    if (!f) return;
    setError(null); setName(f.name);
    try {
      const d = parseDxf(await readDxfText(f));
      const fs = findFaces(d.segments, d.loops);
      setDrawing(d); setFaces(fs); setSelected(new Set(fs.length ? [0] : []));
      if (!fs.length) setError(t("Kapalı bölge bulunamadı: kesit çizgileri uç uca değmiyor olabilir (uçları birleştirip yeniden kaydedin) ya da dosyada yalnız ölçü/metin var."));
    } catch (e) { setDrawing(null); setFaces([]); setError(e instanceof Error ? e.message : t("Dosya okunamadı")); }
  };

  const union = useMemo(() => (selected.size ? unionFaces([...selected].sort((a, b) => a - b).map((i) => faces[i]!)) : null), [selected, faces]);
  const extent = union && "points" in union ? extentOf(union.points) : 0;
  const factor = unit === "auto" ? (unitsToMetre(drawing?.insunits ?? null) ?? guessUnitFactor(extent)) : UNIT_FACTOR[unit];
  const scaled: Pt2[] | null = union && "points" in union ? union.points.map((p) => ({ x: p.x * factor, y: p.y * factor })) : null;
  const check = scaled ? closeSketch(scaled) : null;
  const B = scaled ? Math.max(...scaled.map((p) => p.x)) - Math.min(...scaled.map((p) => p.x)) : 0;
  const H = scaled ? Math.max(...scaled.map((p) => p.y)) - Math.min(...scaled.map((p) => p.y)) : 0;
  const unitLabel = factor === 0.001 ? "mm" : factor === 0.01 ? "cm" : factor === 1 ? "m" : "inç";

  const doImport = (): void => {
    if (!check || "errors" in check) return;
    updateWall(wall.id, check.recipe);
    setPropTab("geometry"); setViewBox(null);
    pushHistory({ text: `DXFAL  ${name}: ${check.polygon.length} köşe, B = ${fmt(B, 2)} m, H = ${fmt(H, 2)} m (${unitLabel})${check.bodyBaseY !== undefined ? `, temel üstü ${fmt(check.bodyBaseY, 2)} m` : ""} → hesap yenilendi`, tone: "ok" });
    onClose();
  };
  const toggle = (i: number): void => setSelected((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });

  return (
    <CadDialog open={open} onClose={onClose} title={t("DXF'ten kesit al")} width={760}
      footer={<><button type="button" className="cad-btn-box" onClick={onClose}>{t("Vazgeç")}</button><button type="button" className="cad-btn-primary disabled:opacity-50" disabled={!check || "errors" in check} onClick={doImport}>{t("İçe aktar")}</button></>}>
      <div className="grid gap-2 text-[11px]">
        <div className="flex flex-wrap items-center gap-2">
          <label className="cad-btn-box cursor-pointer">{t("DXF dosyası seç")}<input type="file" accept=".dxf" className="sr-only" aria-label={t("DXF dosyası seç")} onChange={(e) => void onFile(e.target.files?.[0])} /></label>
          {name ? <span className="font-mono text-cad-ink2">{name}</span> : <span className="text-cad-ink3">{t("ASCII DXF (R12–2018); DWG desteklenmez — AutoCAD'de 'Farklı kaydet → DXF'.")}</span>}
          <label className="ml-auto flex items-center gap-1">{t("Birim")}
            <select className="cad-input font-mono" aria-label={t("Çizim birimi")} value={unit} onChange={(e) => setUnit(e.target.value as UnitChoice)}>
              <option value="auto">{t("otomatik")} ({unitLabel})</option><option value="mm">mm</option><option value="cm">cm</option><option value="m">m</option><option value="in">inç</option>
            </select>
          </label>
        </div>
        {error ? <p role="alert" className="rounded-[2px] border border-bad bg-cad-badbg text-bad px-2 py-1">{error}</p> : null}
        {drawing ? (<>
          <Preview drawing={drawing} faces={faces} selected={selected} onToggle={toggle} />
          <p className="text-cad-ink3">{t("Sarı bölge içe aktarılır; bölgeye tıklayarak seçimi değiştirin (bitişik bölgeler birleşir: gövde + temel). Katmanlar:")} {drawing.layers.join(", ")} · {drawing.entityCount} {t("varlık")}, {faces.length} {t("kapalı bölge")}.</p>
          {union && "error" in union ? <p role="alert" className="text-bad">{union.error}</p> : null}
          {check ? ("errors" in check
            ? <p role="alert" className="text-bad">{t("Kesit motor için geçerli değil:")} {check.errors.join("; ")}</p>
            : <p className="text-cad-cmdok" data-testid="dxf-summary">{t("Seçim:")} {check.polygon.length} {t("köşe")} · B = {fmt(B, 2)} m · H = {fmt(H, 2)} m{check.bodyBaseY !== undefined ? ` · ${t("temel üstü")} ${fmt(check.bodyBaseY, 2)} m` : ""} · {t("burun (0; 0)'a taşınır")}</p>) : null}
        </>) : null}
      </div>
    </CadDialog>
  );
}

function Preview({ drawing, faces, selected, onToggle }: { drawing: DxfDrawing; faces: Face[]; selected: Set<number>; onToggle: (i: number) => void }): JSX.Element {
  const W = 720, Hh = 360;
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const s of drawing.segments) for (const p of [s.a, s.b]) { x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); }
  const w = Math.max(x1 - x0, 1e-9), h = Math.max(y1 - y0, 1e-9);
  const k = Math.min((W - 16) / w, (Hh - 16) / h);
  const sx = (x: number): number => 8 + (x - x0) * k, sy = (y: number): number => Hh - 8 - (y - y0) * k;
  const segs = drawing.segments.slice(0, MAX_SEGMENTS);
  return (
    <svg viewBox={`0 0 ${W} ${Hh}`} width="100%" role="img" aria-label={t("DXF önizleme")} className="bg-cad-model border border-cad-line">
      <path d={segs.map((s) => `M${sx(s.a.x).toFixed(1)} ${sy(s.a.y).toFixed(1)}L${sx(s.b.x).toFixed(1)} ${sy(s.b.y).toFixed(1)}`).join("")} stroke="#6E7680" strokeWidth={0.8} fill="none" />
      {faces.map((f, i) => (
        <polygon key={i} data-face={i} data-selected={selected.has(i) || undefined} points={f.points.map((p) => `${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ")}
          fill={selected.has(i) ? "#FFD166" : "#4C8BC9"} fillOpacity={selected.has(i) ? 0.45 : 0.12} stroke={selected.has(i) ? "#FFD166" : "#4C8BC9"} strokeWidth={selected.has(i) ? 1.5 : 0.8} strokeDasharray={selected.has(i) ? undefined : "4 3"}
          style={{ cursor: "pointer" }} onClick={() => onToggle(i)}><title>{t("Bölge")} {i + 1}</title></polygon>
      ))}
      {drawing.segments.length > MAX_SEGMENTS ? <text x={10} y={16} fill="#D9584B" fontSize={11}>{t("Önizleme kısaltıldı (çok fazla çizgi)")}</text> : null}
    </svg>
  );
}
